import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { goodsReturnsToSupplier } from '@/api/goodsReturns';
import { taxInvoicesIncoming } from '@/api/taxInvoices';
import Drawer from '@/components/Drawer';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);

export default function GoodsReturnToSupplierViewer() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();

  const [rkOpen, setRkOpen] = useState(false);
  const [rkInvoiceId, setRkInvoiceId] = useState('');
  const [rkNumber, setRkNumber] = useState('');
  const [rkDate, setRkDate] = useState(today());
  const [rkRegistered, setRkRegistered] = useState(true);

  const docQ = useQuery({
    queryKey: ['grts', id],
    queryFn: () => goodsReturnsToSupplier.get(id!),
    enabled: !!id,
  });
  const post = useMutation({
    mutationFn: () => goodsReturnsToSupplier.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grts', id] });
      qc.invalidateQueries({ queryKey: ['grts-list'] });
    },
  });
  const unpost = useMutation({
    mutationFn: () => goodsReturnsToSupplier.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grts', id] });
      qc.invalidateQueries({ queryKey: ['grts-list'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => goodsReturnsToSupplier.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grts-list'] });
      navigate('/goods-returns-to-supplier');
    },
  });

  // Список входящих НН по тому же поставщику
  const invoicesQ = useQuery({
    queryKey: ['incoming-invoices-for-rk', docQ.data?.contractor_id],
    queryFn: () => taxInvoicesIncoming.list({
      contractor_id: docQ.data?.contractor_id,
      posted: true,
      limit: 200,
    }),
    enabled: rkOpen && !!docQ.data?.contractor_id,
  });

  const createRk = useMutation({
    mutationFn: () => taxInvoicesIncoming.createCorrectionFromReturn({
      organization_id: docQ.data!.organization_id,
      return_id: id!,
      corrected_invoice_id: rkInvoiceId,
      invoice_number: rkNumber,
      invoice_date: rkDate,
      is_registered_erpn: rkRegistered,
      registration_date: rkRegistered ? rkDate : undefined,
    }),
    onSuccess: (rk) => {
      setRkOpen(false);
      navigate(`/tax-invoices-incoming/${rk.id}`);
    },
  });

  const d = docQ.data;
  if (!d) return <div className="card">Завантаження...</div>;
  const isPosted = d.posted;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Повернення постачальнику №{d.document_number}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ {t('mo.posted_yes')}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <DocumentToolbar
            onBackToList={() => navigate('/goods-returns-to-supplier')}
            onSave={() => {}}
            saveDisabled
            isPosted={isPosted}
            onPost={() => post.mutate()}
            postPending={post.isPending}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => {
              confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); });
            }}
            deletePending={remove.isPending}
          />
          {isPosted && (
            <button onClick={() => setRkOpen(true)}>+ Створити РК</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
        <div className="field"><label>Дата</label><input value={new Date(d.document_date).toLocaleString('uk-UA')} readOnly /></div>
        <div className="field"><label>Сума без ПДВ</label><input value={fmt(d.amount_without_vat)} readOnly style={{ textAlign: 'right' }} /></div>
        <div className="field"><label>Усього з ПДВ</label><input value={fmt(d.total_amount)} readOnly style={{ textAlign: 'right', fontWeight: 600 }} /></div>
      </div>
      <div className="field">
        <label>Документ-підстава</label>
        <input value={d.base_receipt_id ? `doc.goods_receipts / ${d.base_receipt_id}` : '—'} readOnly />
      </div>
      {d.comment && (
        <div className="field"><label>Коментар</label><input value={d.comment} readOnly /></div>
      )}

      <h3 style={{ marginTop: '1.5rem' }}>Рядки</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem', width: 100, textAlign: 'right' }}>Кількість</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>Ціна</th>
            <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Сума без ПДВ</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>ПДВ</th>
            <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Усього</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it) => (
            <tr key={it.id} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{Number(it.quantity).toLocaleString('uk-UA')}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.price)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.amount_without_vat)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.vat_amount)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmt(it.total_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Drawer
        open={rkOpen}
        title="Розрахунок корегування до НН (вхідної)"
        onClose={() => setRkOpen(false)}
        footer={
          <>
            <button onClick={() => setRkOpen(false)}>{t('common.cancel')}</button>
            <button
              className="primary"
              disabled={!rkInvoiceId || !rkNumber || createRk.isPending}
              onClick={() => createRk.mutate()}
            >
              Створити РК
            </button>
          </>
        }
      >
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
          РК до НН вхідної — коригує підтверджений податковий кредит.
          Сума: {fmt(d.total_amount)} грн.
        </p>
        <div className="field">
          <label>Виправлювана НН (вхідна, від цього постачальника)</label>
          <select value={rkInvoiceId} onChange={(e) => setRkInvoiceId(e.target.value)}>
            <option value="">—</option>
            {invoicesQ.data?.items.filter((i) => i.posted).map((i) => (
              <option key={i.id} value={i.id}>
                №{i.invoice_number} від {new Date(i.invoice_date).toLocaleDateString('uk-UA')} — {fmt(i.total_amount)}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>№ РК постачальника</label>
            <input value={rkNumber} onChange={(e) => setRkNumber(e.target.value)} />
          </div>
          <div className="field">
            <label>Дата РК</label>
            <DatePopover value={rkDate} onChange={(v) => setRkDate(v)} />
          </div>
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={rkRegistered}
              onChange={(e) => setRkRegistered(e.target.checked)}
              style={{ width: 'auto', marginRight: '0.5rem' }}
            />
            Зареєстрована в ЄРПН
          </label>
        </div>
      </Drawer>
    </div>
  );
}
